import { useEffect, useRef, useState } from "react";
import { api, fmtErr } from "../../lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Flask, Trash, PencilSimple, UploadSimple, BracketsCurly, X, Download } from "@phosphor-icons/react";
import { toast } from "sonner";

const BASE = process.env.REACT_APP_BACKEND_URL;

const EMPTY = { name: "", kind: "expression", expression: "", code: "", unit: "", description: "", enabled: true };

export default function CustomCalcsPanel({ open, onOpenChange, projectId, industry, onChanged }) {
  const [calcs, setCalcs] = useState([]);
  const [role, setRole] = useState("");
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [vars, setVars] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const bulkRef = useRef(null);
  const canPython = ["admin", "lead"].includes(role);

  const onBulk = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("industry", industry);
    fd.append("file", file);
    try {
      const { data } = await api.post("/custom-calcs/upload", fd);
      const parts = [`${data.created} created`];
      if (data.skipped?.length) parts.push(`${data.skipped.length} skipped (duplicates)`);
      if (data.errors?.length) parts.push(`${data.errors.length} invalid`);
      toast.success(`Upload complete — ${parts.join(", ")}`, { duration: 6000 });
      (data.errors || []).forEach((x) => toast.error(`Row ${x.row}${x.name ? ` (${x.name})` : ""}: ${x.error}`, { duration: 9000 }));
      await load();
      onChanged?.();
    } catch (err) { toast.error(fmtErr(err), { duration: 8000 }); }
  };

  const load = async () => {
    if (!industry) return;
    try {
      const { data } = await api.get(`/custom-calcs`, { params: { industry } });
      setCalcs(data.calcs || []);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY); setEditId(null); setTestResult(null); setVars(null);
    load();
    api.get("/auth/me").then(({ data }) => setRole(data.role || "")).catch(() => {});
  }, [open, industry]);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setTestResult(null); };

  const loadVars = async () => {
    if (vars) { setVars(null); return; }
    try {
      const { data } = await api.get(`/custom-calcs/context/${projectId}`);
      setVars(data.variables || []);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => set("code", String(reader.result || ""));
    reader.readAsText(file);
    e.target.value = "";
  };

  const runTest = async () => {
    try {
      const { data } = await api.post(`/custom-calcs/test`, {
        project_id: projectId, kind: form.kind,
        expression: form.kind === "expression" ? form.expression : null,
        code: form.kind === "python" ? form.code : null,
      });
      setTestResult(data);
    } catch (e) { setTestResult({ error: fmtErr(e) }); }
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      const body = { industry, name: form.name.trim(), kind: form.kind,
        expression: form.kind === "expression" ? form.expression : null,
        code: form.kind === "python" ? form.code : null,
        unit: form.unit, description: form.description, enabled: form.enabled };
      if (editId) await api.put(`/custom-calcs/${editId}`, body);
      else await api.post(`/custom-calcs`, body);
      toast.success(editId ? "Custom calculation updated" : "Custom calculation added — recalculating");
      setForm(EMPTY); setEditId(null); setTestResult(null);
      await load();
      onChanged?.();
    } catch (e) { toast.error(fmtErr(e), { duration: 7000 }); }
    finally { setSaving(false); }
  };

  const startEdit = (c) => {
    setEditId(c.id);
    setForm({ name: c.name, kind: c.kind, expression: c.expression || "", code: c.code || "",
      unit: c.unit || "", description: c.description || "", enabled: c.enabled !== false });
    setTestResult(null);
  };

  const toggleEnabled = async (c) => {
    try {
      await api.put(`/custom-calcs/${c.id}`, { industry: c.industry, name: c.name, kind: c.kind,
        expression: c.expression, code: c.code, unit: c.unit || "", description: c.description || "",
        enabled: c.enabled === false });
      await load();
      onChanged?.();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const del = async (c) => {
    try {
      await api.delete(`/custom-calcs/${c.id}`);
      toast.success(`Deleted "${c.name}"`);
      setConfirmDel(null);
      await load();
      onChanged?.();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[var(--c-surface)] border-[var(--c-border)] text-[var(--c-text)] max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="custom-formulas-dialog">
        <DialogHeader>
          <DialogTitle className="text-[var(--c-text)] text-sm">Custom Formulas — {industry?.replace(/_/g, " ")}</DialogTitle>
          <DialogDescription className="text-[var(--c-muted)] text-xs">
            Organization-level proprietary calculations. Results are appended under a "Custom Calculations" section — built-in outputs can never be overridden.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div data-testid="custom-calc-list">
            <div className="flex items-center mb-2">
              <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)]">Defined formulas ({calcs.length})</div>
              <div className="ml-auto flex gap-1.5">
                <a href={`${BASE}/api/custom-calcs/template`} download data-testid="cc-template-btn"
                  className="h-7 px-2 rounded-sm border border-[var(--c-border)] text-[10px] text-[var(--c-muted)] hover:text-[var(--c-text)] flex items-center gap-1">
                  <Download size={11} /> Template
                </a>
                <button data-testid="cc-bulk-upload-btn" onClick={() => bulkRef.current?.click()}
                  className="h-7 px-2 rounded-sm border border-[var(--c-border)] text-[10px] text-[var(--c-muted)] hover:text-[var(--c-text)] flex items-center gap-1">
                  <UploadSimple size={11} /> Upload Excel/CSV
                </button>
                <input ref={bulkRef} type="file" accept=".xlsx,.csv" className="hidden" onChange={onBulk} data-testid="cc-bulk-file" />
              </div>
            </div>
            {calcs.length === 0 && <div className="text-xs text-[var(--c-muted)] border border-dashed border-[var(--c-border)] rounded-sm p-4" data-testid="custom-calc-empty">No custom formulas yet for this industry.</div>}
            {calcs.map((c) => (
              <div key={c.id} className="flex items-center gap-2 border border-[var(--c-border)] rounded-sm px-3 py-2 mb-1.5" data-testid={`custom-calc-row-${c.id.slice(0, 8)}`}>
                <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded-sm ${c.kind === "python" ? "bg-[var(--c-amber)]/15 text-[var(--c-amber)]" : "bg-[var(--c-accent)]/15 text-[var(--c-accent)]"}`}>
                  {c.kind === "python" ? "PY" : "EXPR"}
                </span>
                <div className="min-w-0">
                  <div className="text-xs text-[var(--c-text)] truncate">{c.name}{c.unit && <span className="text-[var(--c-muted)]"> · {c.unit}</span>}</div>
                  <div className="font-mono text-[10px] text-[var(--c-muted)] truncate">{c.key} = {c.kind === "python" ? "calculate(ctx)" : c.expression}</div>
                </div>
                <div className="ml-auto flex items-center gap-1 shrink-0">
                  <button onClick={() => toggleEnabled(c)} data-testid={`cc-toggle-${c.id.slice(0, 8)}`}
                    className={`text-[9px] font-mono px-1.5 py-0.5 rounded-sm border ${c.enabled !== false ? "border-[var(--c-green)]/50 text-[var(--c-green)]" : "border-[var(--c-border)] text-[var(--c-muted)]"}`}>
                    {c.enabled !== false ? "ON" : "OFF"}
                  </button>
                  <button onClick={() => startEdit(c)} data-testid={`cc-edit-${c.id.slice(0, 8)}`}
                    className="p-1 text-[var(--c-muted)] hover:text-[var(--c-text)]"><PencilSimple size={13} /></button>
                  {confirmDel === c.id ? (
                    <>
                      <button onClick={() => del(c)} data-testid={`cc-delete-confirm-${c.id.slice(0, 8)}`}
                        className="text-[9px] font-mono px-1.5 py-0.5 rounded-sm bg-[var(--c-red)]/15 text-[var(--c-red)]">CONFIRM</button>
                      <button onClick={() => setConfirmDel(null)} className="p-1 text-[var(--c-muted)]"><X size={12} /></button>
                    </>
                  ) : (
                    <button onClick={() => setConfirmDel(c.id)} data-testid={`cc-delete-${c.id.slice(0, 8)}`}
                      className="p-1 text-[var(--c-muted)] hover:text-[var(--c-red)]"><Trash size={13} /></button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="border-t border-[var(--c-border)] pt-3" data-testid="custom-calc-form">
            <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)] mb-2">{editId ? "Edit formula" : "Add formula"}</div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-[10px] text-[var(--c-muted)]">Name</Label>
                <Input data-testid="cc-name-input" value={form.name} onChange={(e) => set("name", e.target.value)}
                  placeholder="e.g. Capitalized Loss Cost" className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-8 text-xs" />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] text-[var(--c-muted)]">Type</Label>
                <Select value={form.kind} onValueChange={(v) => set("kind", v)}>
                  <SelectTrigger data-testid="cc-kind-select" className="h-8 bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[var(--c-surface)] border-[var(--c-border)]">
                    <SelectItem value="expression" className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)] text-xs">Expression (all roles)</SelectItem>
                    <SelectItem value="python" disabled={!canPython} className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)] text-xs">
                      Python script (admin/lead){!canPython && " — restricted"}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {form.kind === "expression" ? (
              <div className="space-y-1 mt-2">
                <Label className="text-[10px] text-[var(--c-muted)]">Expression — numeric result; funcs: sqrt abs min max round log exp pow sin cos tan floor ceil · consts: pi e</Label>
                <Input data-testid="cc-expression-input" value={form.expression} onChange={(e) => set("expression", e.target.value)}
                  placeholder="e.g. no_load_loss_w * 3.0 + load_loss_w * 1.5"
                  className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-8 text-xs font-mono" />
              </div>
            ) : (
              <div className="space-y-1 mt-2">
                <Label className="text-[10px] text-[var(--c-muted)]">Python code — must define calculate(ctx) returning a number. No imports / I/O.</Label>
                <textarea data-testid="cc-code-input" value={form.code} onChange={(e) => set("code", e.target.value)} rows={5}
                  placeholder={"def calculate(ctx):\n    return ctx[\"no_load_loss_w\"] * 3.0"}
                  className="w-full bg-[var(--c-bg)] border border-[var(--c-border)] rounded-sm text-[var(--c-text)] text-xs font-mono p-2 outline-none focus:border-[var(--c-accent)]" />
                <input ref={fileRef} type="file" accept=".py,text/x-python" className="hidden" onChange={onFile} data-testid="cc-code-file" />
                <Button size="sm" variant="outline" data-testid="cc-upload-btn" onClick={() => fileRef.current?.click()}
                  className="border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] hover:bg-[var(--c-surface-2)] rounded-sm h-7 text-[10px]">
                  <UploadSimple size={12} className="mr-1" /> Upload .py file
                </Button>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2 mt-2">
              <div className="space-y-1">
                <Label className="text-[10px] text-[var(--c-muted)]">Unit</Label>
                <Input data-testid="cc-unit-input" value={form.unit} onChange={(e) => set("unit", e.target.value)}
                  placeholder="e.g. USD, kg, %" className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-8 text-xs" />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] text-[var(--c-muted)]">Description</Label>
                <Input data-testid="cc-desc-input" value={form.description} onChange={(e) => set("description", e.target.value)}
                  placeholder="What this computes" className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-8 text-xs" />
              </div>
            </div>

            <div className="flex items-center gap-2 mt-3 flex-wrap">
              <Button size="sm" variant="outline" data-testid="cc-vars-btn" onClick={loadVars}
                className="border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] hover:bg-[var(--c-surface-2)] rounded-sm h-8 text-xs">
                <BracketsCurly size={13} className="mr-1" /> {vars ? "Hide variables" : "Browse variables"}
              </Button>
              <Button size="sm" variant="outline" data-testid="cc-test-btn" onClick={runTest}
                disabled={form.kind === "expression" ? !form.expression.trim() : !form.code.trim()}
                className="border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] hover:bg-[var(--c-surface-2)] rounded-sm h-8 text-xs">
                <Flask size={13} className="mr-1" /> Test on this project
              </Button>
              {testResult && (
                <span data-testid="cc-test-result"
                  className={`font-mono text-xs ${testResult.error ? "text-[var(--c-red)]" : "text-[var(--c-green)]"}`}>
                  {testResult.error ? `Error: ${testResult.error}` : `= ${testResult.value}${form.unit ? ` ${form.unit}` : ""}`}
                </span>
              )}
              <div className="ml-auto flex gap-2">
                {editId && (
                  <Button size="sm" variant="outline" data-testid="cc-cancel-edit" onClick={() => { setEditId(null); setForm(EMPTY); setTestResult(null); }}
                    className="border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] rounded-sm h-8 text-xs">Cancel edit</Button>
                )}
                <Button size="sm" data-testid="cc-save-btn" onClick={save} disabled={saving || !form.name.trim()}
                  className="bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm h-8 text-xs">
                  {saving ? "Saving…" : editId ? "Update" : "Add formula"}
                </Button>
              </div>
            </div>

            {vars && (
              <div className="mt-2 border border-[var(--c-border)] rounded-sm max-h-40 overflow-y-auto" data-testid="cc-vars-list">
                <div className="px-3 py-1.5 text-[9px] font-mono tracking-widest uppercase text-[var(--c-muted)] border-b border-[var(--c-border)]">
                  Available variables ({vars.length}) — click to insert into expression
                </div>
                <div className="grid grid-cols-2 gap-x-4 px-3 py-1">
                  {vars.map((v) => (
                    <button key={v.name} data-testid={`cc-var-${v.name}`} disabled={form.kind !== "expression"}
                      onClick={() => set("expression", (form.expression ? form.expression + " " : "") + v.name)}
                      className="flex justify-between py-0.5 text-left hover:bg-[var(--c-border)]/20 disabled:opacity-60 disabled:cursor-default">
                      <span className="font-mono text-[10px] text-[var(--c-accent)]">{v.name}</span>
                      <span className="font-mono text-[10px] text-[var(--c-muted)]">{typeof v.value === "number" ? Math.round(v.value * 1000) / 1000 : v.value}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
